import { FC } from "react";
import { HoverTooltip } from "@app/dashboard/src/components/shared/HoverTooltip";
import { Icons } from "@app/dashboard/src/components/shared";

/**
 * Info tooltip for Photograph Every Target (the TakeTargetPhotos setting), shared by the read-only device
 * summary and the editable checkbox, styled like OfflineModeInfo.
 *
 * The wording comes from the firmware:
 *   - MovingTargetTracker.CaptureTargetPhotoIfLeaving photographs an approaching target on its first tracked
 *     frame, a departing one once it is 25 m out, and anything left over when the track is lost.
 *   - Each target gets one photo (HasPhotoBeenRequested), and the screen-line / zone / lane-line detectors skip
 *     their own real-time capture while this is on (EventHandlerConfiguration.GlobalTakeTargetPhotos), so an
 *     event's photo is whatever the tracker took — usually before the vehicle reached the line. With a
 *     MinSecondsBetweenPhotos limit the detectors keep capturing, and a vehicle the limit skipped gets its
 *     photo from the event instead (TrackedTarget.PhotoSkippedByRateLimit).
 */
export const TakeTargetPhotosInfo: FC = () => (
    <HoverTooltip hoverable={<Icons.Info className="ml-2 text-brand-primary cursor-help" />}>
        Photographs each tracked vehicle once, as soon as the device starts tracking it. The one exception is a vehicle that is already moving
        away from the device and still within 25 m of it: that one is photographed when it reaches 25 m, so the photo isn&apos;t taken right
        beside the camera. Either way the photo comes from early in the vehicle&apos;s track, not from when it crosses a screen line or enters a
        zone, and because each vehicle gets only one photo, a trigger&apos;s Capture Photo uses this one instead of taking its own.
        Leave this off if you only need photos of events: triggers with Capture Photo then photograph the vehicle at the crossing itself.
    </HoverTooltip>
);
